import { codeToolMaxSecretCount } from "@repo/app-types/entities/tools/code";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { FieldError } from "@repo/ui/field/fieldError";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useId, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";
import { PH_MASK_CLASS } from "../../analytics/sessionRecordingMasking.js";
import {
  buildNewCodeToolFormSecret,
  buildStoredCodeToolFormSecret,
  checkCodeToolFormSecretDomainsChanged,
  secretDomainChangeNeedsValueMessage,
  toggleCodeToolFormSecretDomain,
  type CodeToolFormSecret,
} from "../formConfigs/codeToolSecrets.js";

interface CodeToolSecretsEditorProps {
  secrets: readonly CodeToolFormSecret[];
  /** The tool's allowed domains as the form has them, saved or not. */
  toolDomains: readonly string[];
  onChange: (secrets: CodeToolFormSecret[]) => void;
}

export const CodeToolSecretsEditor: FC<CodeToolSecretsEditorProps> = (
  props,
) => {
  const { secrets, toolDomains, onChange } = props;

  const handleUpdate = (updated: CodeToolFormSecret): void => {
    onChange(
      secrets.map((secret) => (secret.key === updated.key ? updated : secret)),
    );
  };

  const isAtLimit = secrets.length >= codeToolMaxSecretCount;
  const addButton = (
    <Button
      variant={ButtonVariant.Secondary}
      size={Size.MEDIUM}
      icon={<IconPlus className="h-4 w-4" />}
      isDisabled={isAtLimit}
      onClick={() =>
        onChange([
          ...secrets,
          buildNewCodeToolFormSecret(uuidv4(), toolDomains),
        ])
      }
    >
      Add secret
    </Button>
  );

  return (
    <div className="flex flex-col space-y-3">
      <Label size={Size.SMALL}>Secrets</Label>
      <p className="text-sm text-muted-foreground">
        Values the script reads as <code>secrets.NAME</code>, such as API keys.
        Each can only be sent to the domains checked for it, and its value
        can&apos;t be read back once saved.
      </p>

      {secrets.map((secret) => (
        <CodeToolSecretRow
          key={secret.key}
          secret={secret}
          toolDomains={toolDomains}
          onChange={handleUpdate}
          onRemove={() =>
            onChange(secrets.filter((other) => other.key !== secret.key))
          }
        />
      ))}

      <div>
        {isAtLimit ? (
          // A disabled button fires no hover events, so the tooltip hangs off a wrapper.
          <TooltipTrigger>
            <TooltipHoverTarget shape="rounded">{addButton}</TooltipHoverTarget>
            <SimpleTooltip>
              A tool can have up to {codeToolMaxSecretCount} secrets.
            </SimpleTooltip>
          </TooltipTrigger>
        ) : (
          addButton
        )}
      </div>
    </div>
  );
};

interface CodeToolSecretRowProps {
  secret: CodeToolFormSecret;
  toolDomains: readonly string[];
  onChange: (secret: CodeToolFormSecret) => void;
  onRemove: () => void;
}

const CodeToolSecretRow: FC<CodeToolSecretRowProps> = (props) => {
  const { secret, toolDomains, onChange, onRemove } = props;

  const domainsLabelId = useId();
  const { stored, errors } = secret;
  const isValueOpen = !stored || secret.isReplacingValue;
  const isDomainChangePending =
    secret.value === "" && checkCodeToolFormSecretDomainsChanged(secret);

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border-light bg-gray-50 p-3">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          {stored ? (
            <div className="flex h-8 items-center gap-2 text-sm">
              <span className="font-mono font-medium">{secret.name}</span>
              <span
                className={twMerge(
                  "font-mono text-muted-foreground",
                  PH_MASK_CLASS,
                )}
              >
                ••••{stored.value_hint ?? ""}
              </span>
            </div>
          ) : (
            <Input
              label="Name"
              placeholder="API_KEY"
              value={secret.name}
              onChange={(e) =>
                onChange({ ...secret, name: e.target.value, errors: undefined })
              }
              errorDescription={errors?.name}
              maxLength={64}
            />
          )}
        </div>
        <div className={stored ? undefined : "pt-7"}>
          <IconButton
            icon={<IconTrash className="h-4 w-4" />}
            variant={ButtonVariant.Tertiary}
            onClick={onRemove}
            accessibleLabel={`Remove ${secret.name || "secret"}`}
          />
        </div>
      </div>

      <div role="group" aria-labelledby={domainsLabelId}>
        <div
          id={domainsLabelId}
          className="text-xs font-medium text-muted-foreground"
        >
          Send only to
        </div>
        {toolDomains.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">
            Add an allowed domain to the tool first.
          </p>
        ) : (
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {toolDomains.map((domain) => (
              <Checkbox
                key={domain}
                label={domain}
                isChecked={secret.allowedDomains.includes(domain)}
                onChange={(isChecked) =>
                  onChange(
                    toggleCodeToolFormSecretDomain(secret, domain, isChecked),
                  )
                }
              />
            ))}
          </div>
        )}
        {errors?.domains ? <FieldError>{errors.domains}</FieldError> : null}
      </div>

      {isValueOpen ? (
        <div>
          <Input
            type="password"
            label={stored ? "New value" : "Value"}
            value={secret.value}
            onChange={(e) =>
              onChange({ ...secret, value: e.target.value, errors: undefined })
            }
            description={
              isDomainChangePending && !errors?.value
                ? secretDomainChangeNeedsValueMessage
                : undefined
            }
            errorDescription={errors?.value}
            autoComplete="new-password"
            maxLength={4096}
          />
          {stored ? (
            <Button
              variant={ButtonVariant.Tertiary}
              size={Size.SMALL}
              className="mt-1"
              onClick={() => onChange(buildStoredCodeToolFormSecret(stored))}
            >
              Keep current value
            </Button>
          ) : null}
        </div>
      ) : (
        <div>
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.SMALL}
            onClick={() => onChange({ ...secret, isReplacingValue: true })}
          >
            Replace value
          </Button>
        </div>
      )}
    </div>
  );
};
